/**
 * LandingCmsEditor (iter1069) — управление на началната страница.
 *
 * Подредба (влачене или стрелки), скриване, редактиране на всеки текст и
 * смяна на снимките. Работи върху чернова; посетителите виждат промените
 * едва след „Публикувайте“.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Save, Rocket, Eye, EyeOff, ArrowUp, ArrowDown, RotateCcw,
  GripVertical, Upload, ExternalLink, Image as ImageIcon,
} from "lucide-react";
import { COPY } from "@/pages/LandingPage";
import {
  DEFAULT_ORDER, SECTION_META, resolveOrder, collectTextFields,
} from "@/lib/landingSchema";

const LANGS = [
  { id: "bg", label: "Български" },
  { id: "en", label: "Английски" },
  { id: "es", label: "Испански" },
];

const FIELD_LABELS = {
  eyebrow: "Надзаглавие", title: "Заглавие", sub: "Подзаглавие",
  headline_1: "Заглавие, първи ред", headline_2: "Заглавие, втори ред",
  cta: "Надпис на бутона", cta_primary: "Надпис на главния бутон",
  cta_secondary: "Надпис на втория бутон", desc: "Описание", note: "Забележка",
  name: "Име", text: "Текст", label: "Надпис", heading: "Заглавие", body: "Текст",
  learnMore: "Подробно обяснение", linkBg: "Надпис на препратката",
  tagline: "Кратко описание", disclaimer: "Правна забележка",
  privacy: "Надпис към поверителността", placeholder: "Запълващ текст",
  no_card: "Ред за доверие", pricing_teaser: "Ред за цената",
  md_badge: "Знак за съответствие", trial: "Ред за безплатния период",
  ai_label: "Надпис на средния възел", twin_label: "Надпис на близнаците",
  out_label: "Надпис на изхода", learn: "Надпис „научете повече“",
  title_prefix: "Заглавие след числото", stat: "Число", from: "От", to: "Към",
  connections_title: "Заглавие за връзките", connections_sub: "Текст за връзките",
  effectiveness_title: "Заглавие „защо работи“", library_cta: "Надпис към библиотеката",
  lottie_caption: "Надпис под движението", note_title: "Заглавие на бележката",
  note_body: "Текст на бележката", lever: "Къде е лостът",
  filter_all: "Филтър: всички", filter_organs: "Филтър: органи",
  filter_lifestyle: "Филтър: начин на живот", filter_clinical: "Филтър: клинични",
  // надписи в горната лента
  science: "Меню: Наука", pricing: "Меню: Цени", assessment: "Меню: Безплатна оценка",
  guide: "Меню: Ръководство", faq: "Меню: Често задавани въпроси",
  compare: "Меню: Сравнение", signin: "Меню: Вход", signup: "Меню: Регистрация",
  dashboard: "Меню: Моето здраве", how: "Меню: Как работи",
  smart: "Меню: Умно хранене", about: "Меню: За нас",
  consultants: "Меню: Консултанти", tools: "Меню: Средства",
  // надписи по публика
  title_patient: "Заглавие за пациент", title_doctor: "Заглавие за лекар",
  title_clinic: "Заглавие за клиника", sub_patient: "Текст за пациент",
  sub_doctor: "Текст за лекар", sub_clinic: "Текст за клиника",
  cta_patient: "Бутон за пациент", cta_doctor: "Бутон за лекар",
  cta_clinic: "Бутон за клиника", patient: "Раздел „пациент“",
  doctor: "Раздел „лекар“", clinic: "Раздел „клиника“",
};

const prettify = (path) => {
  const parts = path.split(".");
  const last = parts[parts.length - 1];
  const idx = parts.findIndex((p) => /^\d+$/.test(p));
  const nth = idx >= 0 ? ` · ред ${Number(parts[idx]) + 1}` : "";
  if (/^\d+$/.test(last)) return `Ред ${Number(last) + 1}${""}`;
  return `${FIELD_LABELS[last] || last}${nth}`;
};

export default function LandingCmsEditor() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [lang, setLang] = useState("bg");
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [hidden, setHidden] = useState([]);
  const [texts, setTexts] = useState({});
  const [images, setImages] = useState({});
  const [selected, setSelected] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  // Влаченият ред се пази в ref, не в state: иначе при бързо пускане
  // обработчикът на drop още вижда стария (празен) избор.
  const dragRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  // Различаваме „записана чернова" от „публикувано на живо" — иначе
  // надписът „Записано" се чете като „вече е на сайта".
  const [published, setPublished] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/landing/content?state=draft`),
      axios.get(`${API}/landing/content`),
    ])
      .then(([{ data }, { data: live }]) => {
        setOrder(resolveOrder(data?.order));
        setHidden(data?.hidden || []);
        setTexts(data?.texts || {});
        setImages(data?.images || {});
        // Черновата различава ли се от това, което посетителите виждат?
        const shape = (d) => JSON.stringify({
          order: resolveOrder(d?.order), hidden: d?.hidden || [],
          texts: d?.texts || {}, images: d?.images || {},
        });
        setPublished(shape(data) === shape(live));
      })
      .catch(() => toast.error("Черновата не се зареди"))
      .finally(() => setLoading(false));
  }, [API]);

  const meta = SECTION_META[selected] || {};
  const baseCopy = COPY[lang] || COPY.bg;

  const fields = useMemo(() => {
    const rows = [];
    for (const key of meta.copyKeys || []) {
      const node = baseCopy[key] ?? COPY.bg[key];
      if (node !== undefined) rows.push(...collectTextFields(node, key));
    }
    return rows;
  }, [meta.copyKeys, baseCopy]);

  const valueOf = (path, fallback) => {
    const v = texts[lang]?.[path];
    return v === undefined ? fallback : v;
  };

  const setField = (path, value) => {
    setTexts((prev) => {
      const langMap = { ...(prev[lang] || {}) };
      if (value === null) delete langMap[path];
      else langMap[path] = value;
      return { ...prev, [lang]: langMap };
    });
    setDirty(true);
    setPublished(false);
  };

  const move = (id, dir) => {
    setOrder((prev) => {
      const i = prev.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
    setPublished(false);
  };

  const onDrop = (targetId) => {
    const src = dragRef.current;
    if (!src || src === targetId) return;
    setOrder((prev) => {
      const from = prev.indexOf(src);
      const to = prev.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      const next = prev.filter((x) => x !== src);
      // При влачене НАДОЛУ редът застава СЛЕД целта. Иначе влаченето с
      // една позиция надолу не променяше нищо и изглеждаше като дефект.
      const at = next.indexOf(targetId) + (from < to ? 1 : 0);
      next.splice(at, 0, src);
      return next;
    });
    dragRef.current = null;
    setDirty(true);
    setPublished(false);
  };

  const toggleHidden = (id) => {
    setHidden((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setDirty(true);
    setPublished(false);
  };

  const save = async () => {
    setBusy("save");
    try {
      await axios.put(`${API}/admin/landing/content`, { order, hidden, texts, images });
      setDirty(false);
      setPublished(false);
      toast.success("Черновата е записана. Посетителите още виждат публикуваното — "
        + "натиснете „Публикувайте“, за да влезе на живо.", { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Записването не успя");
    } finally { setBusy(""); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await axios.put(`${API}/admin/landing/content`, { order, hidden, texts, images });
      await axios.post(`${API}/admin/landing/publish`);
      setDirty(false);
      setPublished(true);
      toast.success("Началната страница е публикувана. Презаредете я, за да видите промяната.",
        { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Публикуването не успя");
    } finally { setBusy(""); }
  };

  const resetAll = async () => {
    if (!window.confirm("Да върна ли началната страница към текстовете от кода? Черновата и публикуваното ще се изтрият.")) return;
    setBusy("reset");
    try {
      await axios.post(`${API}/admin/landing/reset?state=all`);
      setOrder(DEFAULT_ORDER); setHidden([]); setTexts({}); setImages({});
      setDirty(false); setPublished(true);
      toast.success("Върнати са текстовете от кода");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Връщането не успя");
    } finally { setBusy(""); }
  };

  const resetSection = () => {
    setTexts((prev) => {
      const langMap = { ...(prev[lang] || {}) };
      for (const f of fields) delete langMap[f.path];
      return { ...prev, [lang]: langMap };
    });
    setImages((prev) => {
      const next = { ...prev };
      for (const im of meta.images || []) delete next[im.slot];
      return next;
    });
    setDirty(true);
    setPublished(false);
    toast.success("Секцията е върната към текста от кода");
  };

  const uploadImage = async (slot, file) => {
    if (!file) return;
    setBusy(`img-${slot}`);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/landing/image`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => ({ ...prev, [slot]: data.url }));
      setDirty(true);
      setPublished(false);
      toast.success("Файлът е качен");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Качването не успя");
    } finally { setBusy(""); }
  };

  if (loading) {
    return <div className="p-8 flex justify-center" data-testid="landing-cms-loading">
      <Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const overrideCount = Object.values(texts).reduce((n, m) => n + Object.keys(m || {}).length, 0);

  return (
    <div data-testid="landing-cms-editor" className="space-y-4">
      {/* Лента с действията */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-800">
        <div className="flex gap-1 mr-2">
          {LANGS.map((l) => (
            <button key={l.id} type="button" onClick={() => setLang(l.id)}
              data-testid={`landing-cms-lang-${l.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                lang === l.id ? "bg-emerald-600 text-white border-emerald-600"
                  : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
              {l.label}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={save} disabled={!!busy} data-testid="landing-cms-save">
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Запишете черновата
        </Button>
        <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700"
          onClick={publish} disabled={!!busy} data-testid="landing-cms-publish">
          {busy === "publish" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Rocket className="w-4 h-4 mr-1.5" />}
          Публикувайте
        </Button>
        <Button size="sm" variant="outline" data-testid="landing-cms-preview"
          onClick={() => window.open("/?cms=draft", "_blank")}>
          <ExternalLink className="w-4 h-4 mr-1.5" /> Преглед на черновата
        </Button>
        <Button size="sm" variant="outline" onClick={resetAll} disabled={!!busy}
          data-testid="landing-cms-reset-all">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Върнете текстовете от кода
        </Button>
        <span className={`text-xs ml-auto font-medium ${
          published && !dirty ? "text-emerald-600" : "text-amber-600"}`}
          data-testid="landing-cms-status">
          {dirty
            ? "Незаписани промени"
            : published
              ? "Публикувано — посетителите виждат това"
              : "Записана чернова — още НЕ е на живо"}
          {" · "}{overrideCount} променени текста · {hidden.length} скрити секции
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Подредба */}
        <div className="rounded-xl border p-3 bg-white dark:bg-slate-900 dark:border-slate-800"
          data-testid="landing-cms-order-list">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            Подредба на секциите
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            Влачете реда или ползвайте стрелките. Окото скрива секцията от посетителите.
          </p>
          <div className="space-y-1.5">
            {order.map((id, i) => {
              const m = SECTION_META[id] || { label: id };
              const isHidden = hidden.includes(id);
              return (
                <div key={id} draggable
                  onDragStart={(e) => { dragRef.current = id; if (e.dataTransfer) e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); onDrop(id); }}
                  onDragEnd={() => { dragRef.current = null; }}
                  onClick={() => setSelected(id)}
                  data-testid={`landing-cms-section-${id}`}
                  className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 cursor-pointer transition-colors ${
                    selected === id
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                      : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                  } ${isHidden ? "opacity-50" : ""}`}>
                  <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0 cursor-grab" />
                  <span className="text-[10px] text-slate-400 w-4 shrink-0">{i + 1}</span>
                  <span className="text-xs font-medium truncate flex-1">{m.label}</span>
                  <button type="button" data-testid={`landing-cms-up-${id}`}
                    onClick={(e) => { e.stopPropagation(); move(id, -1); }}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" data-testid={`landing-cms-down-${id}`}
                    onClick={(e) => { e.stopPropagation(); move(id, 1); }}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                  <button type="button" data-testid={`landing-cms-toggle-${id}`}
                    onClick={(e) => { e.stopPropagation(); toggleHidden(id); }}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                    {isHidden ? <EyeOff className="w-3 h-3 text-amber-600" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Редактор на избраната секция */}
        <div className="rounded-xl border p-4 bg-white dark:bg-slate-900 dark:border-slate-800"
          data-testid="landing-cms-section-editor">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <h3 className="text-base md:text-lg font-bold" data-testid="landing-cms-section-title">
                {meta.label || selected}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {fields.length ? `${fields.length} текстови полета` : "Няма редактируем текст"}
                {hidden.includes(selected) ? " · секцията е скрита" : ""}
              </p>
            </div>
            {(fields.length > 0 || (meta.images || []).length > 0) && (
              <Button size="sm" variant="outline" onClick={resetSection}
                data-testid="landing-cms-reset-section">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Върнете тази секция
              </Button>
            )}
          </div>

          {meta.note && (
            <p className="text-xs rounded-lg px-3 py-2 mb-4 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              data-testid="landing-cms-section-note">{meta.note}</p>
          )}

          {/* Снимки */}
          {(meta.images || []).length > 0 && (
            <div className="space-y-3 mb-5" data-testid="landing-cms-images">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Снимки и видео
              </div>
              {meta.images.map((im) => (
                <div key={im.slot} className="rounded-lg border p-3 dark:border-slate-800"
                  data-testid={`landing-cms-image-${im.slot}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs font-semibold">{im.label}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(images[im.slot] || im.fallback) && (
                      <img src={images[im.slot] || im.fallback} alt={im.label}
                        className="h-10 w-16 object-contain rounded bg-slate-100 dark:bg-slate-800" />
                    )}
                    <Input value={images[im.slot] || ""} placeholder={im.fallback || "Адрес на файла"}
                      data-testid={`landing-cms-image-url-${im.slot}`}
                      onChange={(e) => { setImages((p) => ({ ...p, [im.slot]: e.target.value })); setDirty(true); }}
                      className="h-8 text-xs flex-1 min-w-0" />
                    <label className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 dark:border-slate-700">
                      {busy === `img-${im.slot}`
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Upload className="w-3.5 h-3.5" />}
                      Качете
                      <input type="file" className="hidden"
                        data-testid={`landing-cms-image-upload-${im.slot}`}
                        accept="image/*,video/mp4,video/webm"
                        onChange={(e) => uploadImage(im.slot, e.target.files?.[0])} />
                    </label>
                    {images[im.slot] && (
                      <Button size="sm" variant="ghost" className="h-8 px-2"
                        data-testid={`landing-cms-image-clear-${im.slot}`}
                        onClick={() => { setImages((p) => { const n = { ...p }; delete n[im.slot]; return n; }); setDirty(true); }}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Текстове */}
          <div className="space-y-3" data-testid="landing-cms-fields">
            {fields.map((f) => {
              const current = valueOf(f.path, f.value);
              const changed = texts[lang]?.[f.path] !== undefined;
              return (
                <div key={f.path} data-testid={`landing-cms-field-${f.path}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="text-xs font-semibold">
                      {prettify(f.path)}
                      {changed && <span className="ml-1.5 text-[10px] text-emerald-600">променено</span>}
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="text-[10px] text-slate-400">{f.path}</code>
                      {changed && (
                        <button type="button" onClick={() => setField(f.path, null)}
                          data-testid={`landing-cms-field-reset-${f.path}`}
                          className="text-[10px] text-slate-500 hover:text-slate-900 underline">
                          върнете
                        </button>
                      )}
                    </div>
                  </div>
                  {f.long ? (
                    <Textarea value={current} rows={3} className="text-xs"
                      data-testid={`landing-cms-input-${f.path}`}
                      onChange={(e) => setField(f.path, e.target.value)} />
                  ) : (
                    <Input value={current} className="h-8 text-xs"
                      data-testid={`landing-cms-input-${f.path}`}
                      onChange={(e) => setField(f.path, e.target.value)} />
                  )}
                </div>
              );
            })}
            {!fields.length && !(meta.images || []).length && (
              <p className="text-sm text-slate-500">
                Тази секция може само да се мести и да се скрива.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
